/**
 * Counted worked minutes for the "Adjust times" preview — mirrors the server's
 * `taskActualMinutes` (apps/api/src/lib/time.ts): the span capped per business
 * day (DEFAULT_WORKDAY_HOURS) so an overnight span reads as one workday rather
 * than raw wall-clock (a 26h overnight → 16h, not 26h). This is the number that
 * lands on the recap, so the panel shows it instead of the raw elapsed.
 *
 * Weekend days are dropped ONLY when they aren't part of the task's committed
 * schedule (`scheduledDays`, local YYYY-MM-DD) — a genuine Saturday install
 * counts in full, while a weekday task left running across a weekend has those
 * stray days dropped. Mirrors the server's schedule-aware weekend guard.
 *
 * Computed in the browser's local zone. Vari operates in a single business
 * timezone (America/Chicago), so local == business for every user; the recap
 * (server-side, BUSINESS_TZ) remains the source of truth.
 */
const DEFAULT_WORKDAY_HOURS = 8;

function startOfLocalDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Local YYYY-MM-DD for a timestamp — the key format used by `scheduledDays`. */
function localDayString(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * The set of local day-strings a task is scheduled to work, spanning
 * [scheduledStart, scheduledEnd]. Weekend days in this set count as real work.
 */
export function localScheduledDays(
  scheduledStart?: string | null,
  scheduledEnd?: string | null,
): Set<string> {
  const days = new Set<string>();
  if (!scheduledStart) return days;
  const startMs = new Date(scheduledStart).getTime();
  const endMs = scheduledEnd ? new Date(scheduledEnd).getTime() : startMs;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return days;
  let dayStart = startOfLocalDay(startMs);
  const lastDay = startOfLocalDay(endMs);
  let guard = 0;
  while (dayStart <= lastDay && guard < 400) {
    days.add(localDayString(dayStart));
    dayStart = startOfLocalDay(dayStart + 26 * 3_600_000);
    guard += 1;
  }
  return days;
}

export function countedWorkedMinutes(
  startMs: number,
  finishMs: number,
  capHours: number = DEFAULT_WORKDAY_HOURS,
  scheduledDays?: ReadonlySet<string>,
): number {
  if (!Number.isFinite(startMs) || !Number.isFinite(finishMs) || finishMs <= startMs) return 0;
  let minutes = 0;
  let dayStart = startOfLocalDay(startMs);
  const lastDay = startOfLocalDay(finishMs);
  let guard = 0;
  while (dayStart <= lastDay && guard < 400) {
    // +26h then re-normalize → the next local midnight, DST-safe (a 25h fall-back
    // day + 24h would land back on the same day and loop).
    const nextDay = startOfLocalDay(dayStart + 26 * 3_600_000);
    const dow = new Date(dayStart).getDay(); // 0 Sun … 6 Sat
    const isWeekend = dow === 0 || dow === 6;
    const weekendButScheduled = isWeekend && (scheduledDays?.has(localDayString(dayStart)) ?? false);
    if (!isWeekend || weekendButScheduled) {
      const effStart = Math.max(startMs, dayStart);
      const effEnd = Math.min(finishMs, nextDay);
      const hours = Math.max(0, (effEnd - effStart) / 3_600_000);
      const spansBeyondDay = startMs < dayStart || finishMs > nextDay;
      minutes += (spansBeyondDay ? Math.min(hours, capHours) : hours) * 60;
    }
    dayStart = nextDay;
    guard += 1;
  }
  return Math.round(minutes);
}
